/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pick, omit, sortBy, pickBy } from 'lodash';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from '../../../organisms/crud/form-types';

import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import moment from 'moment';
import { TFunction } from 'react-i18next';
import { formFields, serializeIcon } from './form-fields';
import { Drug } from 'app/types/drug';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import { DrugIcons } from 'app/components/atoms/drug-icon/drug-icon';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<Drug> => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'id',
    key: 'id',
    size: '100px',
    render: item => <IdCopy>{item.id}</IdCopy>
  },
  {
    title: translate('common.table.columns.registry'),
    dataIndex: 'registry',
    key: 'registry',
    size: '100px'
  },
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    supportLocale: true,
    key: 'name',
    size: '1fr',
    render: item => item.name
  },
  {
    title: translate('common.table.columns.productName'),
    dataIndex: 'productName',
    supportLocale: true,
    key: 'productName',
    size: '1fr',
    render: item => item.productName
  },
  {
    title: translate('common.table.columns.productDescription'),
    dataIndex: 'productDescription',
    supportLocale: true,
    key: 'productDescription',
    size: '1fr',
    render: item => item.productDescription
  },
  {
    title: translate('common.table.columns.therapeuticClassificationCode'),
    dataIndex: 'therapeuticClassificationCode',
    key: 'therapeuticClassificationCode',
    size: '150px',
    render: item => item.therapeuticClassificationCode ?? 'N/A'
  },
  {
    title: translate('common.table.columns.cci'),
    dataIndex: 'cci',
    key: 'cci',
    size: '150px'
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '200px',
    render: item => <DateTime date={item.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    size: '200px',
    render: item => <DateTime date={item.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  }
];

/**
 * List.
 */

const list = (translate: TFunction): CrudListType<Drug> => ({
  columns: listColumns(translate),
  filters: listFilters(translate),
  languages: '*',
  enableExport: true,
  defaultQueryString: 'filter=%7B"guestAccount"%3A"false"%7D&filterOpened=false',
  resolveParams: (params: any, item?: Drug) => {
    return {
      id: params?.id ?? item?.id
    };
  },
  expandedDataKey: item => sortBy(item.transactions, 'date').reverse(),
  route: appRoutes.dashboard.medical.drugs,
  addButton: translate('common.actions.add'),
  canEdit: true,
  canRemove: true,
  key: ['drugs'],
  normalizeFilters: (data: any) => {
    const { ...filters } = data.filters;
    const result: any = {
      filters: pickBy(filters, identity),
      order: []
    };

    result.order.push({ key: 'name', direction: 'desc' });

    return result;
  },
  normalizeData: (result: any) => {
    return result;
  },
  pagination: {
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100, 500, 1000]
  },
  endpoint: apiEndpoints.drugs,
  endpointById: apiEndpoints.drugsById
});

/**
 * Remove.
 */

const remove = (): CrudRemoveType<Drug> => ({
  endpoint: apiEndpoints.drugsDelete
});

/**
 * Normalize payload.
 */

function normalizePayload(values: any) {
  const result = omit(values, ['id', 'createdAt', 'updatedAt']);

  result.chronic = !!result.chronic;
  result.active = !!result.active;
  result.requiresPrescription = !!result.requiresPrescription;
  result.therapeuticClassificationCode = String(result.therapeuticClassificationCode);

  // Keep CCI field - it will be handled by backend listeners
  // The backend will remove it before saving to drugs table

  // Extract iconName and iconNumber from the combined icon field
  if (result.icon) {
    try {
      const icon = JSON.parse(result.icon);
      result.iconName = icon.name;
      result.iconNumber = icon.number;
    } catch (e) {
      // If icon is not valid JSON, leave iconName and iconNumber unchanged
    }
    delete result.icon;
  }

  return result;
}

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType<Drug> => ({
  formFields: formFields(translate, 'add'),
  languages: '*',
  acceptQueryFields: [
    {
      name: 'accountId',
      parser: value => (!!value ? parseInt(value, 10) : undefined)
    }
  ],
  normalizePayload
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType<Drug> => ({
  formFields: formFields(translate, 'edit'),
  languages: '*',
  endpoint: apiEndpoints.drugsById,
  redirect: appRoutes.dashboard.medical.drugs,
  resolveParams: (params: any) => params,
  route: appRoutes.dashboard.medical.drugsEdit,
  submitLabel: translate('common.actions.edit'),
  normalizePayload,
  normalizeInitialValues: (keys: string[], payload: any) => {
    if (!payload || !payload.id) {
      return undefined; // Return undefined to prevent form reset
    }

    // Include iconName and iconNumber in the keys so they are picked from payload
    const keysWithIcon = [...keys, 'iconName', 'iconNumber'];
    const data: any = pick(payload, keysWithIcon);

    data.createdAt = moment(data.createdAt).format('YYYY-MM-DD HH:mm:ss');
    data.updatedAt = moment(data.updatedAt).format('YYYY-MM-DD HH:mm:ss');

    // Convert iconName and iconNumber back to the combined icon field for the select
    // Use serializeIcon to ensure consistent JSON format matching the options
    if (data.iconNumber) {
      // Ensure iconNumber is a number (API might return string)
      const iconNumber = typeof data.iconNumber === 'string' ? parseInt(data.iconNumber, 10) : data.iconNumber;

      // Find the correct icon by number (iconName might be outdated in the database)

      const correctIcon = DrugIcons.find(icon => icon.number === iconNumber);
      const iconName = correctIcon ? correctIcon.name : data.iconName;

      data.icon = serializeIcon(iconName, iconNumber);
    }

    return data;
  }
});

/**
 * Config.
 */

export function createDrugsTemplate(translate: TFunction): CrudTemplate<Drug> {
  return normalizeTemplate(translate, {
    add: add(translate),
    edit: edit(translate),
    list: list(translate),
    remove: remove()
  });
}
